<template>
  <div class="with-pagination" ref="root">
    <SectionGroup gap="medium">
      <slot />
      <SectionBox v-if="pagination.data.meta.totalPages > 1">
        <div class="with-pagination__controls">
          <div class="with-pagination__side-button">
            <BasicButton
              :disabled="!previousPage.value"
              :href="previousPage.href"
              rel="prev"
              appearance="pagination-button"
              @click.prevent="onClick($event); pagination.replacePrevPage()"
            >
              <ArrowIcon direction="left" size="28" />
            </BasicButton>
          </div>
          <div class="with-pagination__pages">
            <div
              v-if="hasStartContinuation"
              class="with-pagination__continuation"
            >...</div>
            <BasicButton
              v-for="page in pages"
              :key="page.href"
              :href="page.href"
              :appearance="page.active ? 'pagination-button-active' : 'pagination-button'"
              @click.prevent="onClick($event); pagination.loadAndReplaceImmediate({ page: page.value })"
            >
              <span v-text="page.value" />
            </BasicButton>
            <div
              v-if="hasEndContinuation"
              class="with-pagination__continuation"
            >...</div>
          </div>
          <div class="with-pagination__side-button">
            <BasicButton
              :disabled="!nextPage.value"
              :href="nextPage.href"
              rel="next"
              appearance="pagination-button"
              @click.prevent="onClick($event); pagination.replaceNextPage()"
            >
              <ArrowIcon direction="right" size="28" />
            </BasicButton>
          </div>
        </div>
      </SectionBox>
    </SectionGroup>
  </div>
</template>

<script>
  import queryString from 'query-string';

  import BasicButton from 'src/components/BasicButton/BasicButton.vue';
  import ArrowIcon from 'src/components/icons/ArrowIcon/ArrowIcon.vue';
  import SectionBox from 'src/components/SectionBox/SectionBox.vue';
  import SectionGroup from 'src/components/sections/SectionGroup/SectionGroup.vue';

  export default {
    name: 'WithPagination',
    components: { SectionBox, SectionGroup, BasicButton, ArrowIcon },
    inject: ['mediaQueries', 'pagination'],
    props: {
      maxPages: {
        type: Number,
        default: 9,
      },
      params: Object,
      scrollToTop: Boolean,
    },
    methods: {
      onClick(event) {
        this.$emit('click', event);

        if (this.scrollToTop) {
          this.$refs.root.scrollIntoView({
            block: 'start',
            inline: 'nearest',
            behavior: 'auto'
          });
        }
      },
    },
    computed: {
      previousPage() {
        const { currentPage } = this.pagination.data.meta;
        if (currentPage === 1) return { href: null, value: null };
        const page = currentPage - 1;
        return {
          value: page,
          href: `?${queryString.stringify({ ...this.params, page }, { arrayFormat: 'bracket' })}`,
        };
      },
      nextPage() {
        const { totalPages, currentPage } = this.pagination.data.meta;
        if (currentPage === totalPages) return { href: null, value: null };
        const page = currentPage + 1;
        return {
          value: page,
          href: `?${queryString.stringify({ ...this.params, page }, { arrayFormat: 'bracket' })}`,
        };
      },
      pages() {
        const { maxPages, mediaQueries } = this;
        const { totalPages, currentPage } = this.pagination.data.meta;
        const max = mediaQueries.mobile ? 5 : maxPages;
        let start = currentPage;
        let end = currentPage;
        for (let i = 0; i < max; i += 1) {
          if (i % 2 === 0) {
            if (start > 0) start -= 1;
          } else if (end < totalPages) end += 1;
        }
        const length = end - start;

        return new Array(length).fill(null).map((value, index) => {
          const page = start + index + 1;
          return {
            value: page,
            active: page === currentPage,
            href: `?${queryString.stringify({ ...this.params, page }, { arrayFormat: 'bracket' })}`
          }
        });
      },
      hasStartContinuation() {
        const firstPage = this.pages[0];
        if (!firstPage) return false;
        return firstPage.value > 1;
      },
      hasEndContinuation() {
        const lastPage = this.pages[this.pages.length-1];
        if (!lastPage) return false;
        return lastPage.value < this.pagination.data.meta.totalPages;
      },
    }
  }
</script>